import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Zap, Code, Shield, Headphones } from 'lucide-react';

const benefits = [
  {
    icon: Zap,
    title: 'Fast Setup',
    description: 'Launch your first AI assistant in minutes with our visual flow builder',
    gradient: 'from-purple-500 to-pink-500',
  },
  {
    icon: Code,
    title: 'No-Code Platform',
    description: 'Build complex conversation flows without writing a single line of code',
    gradient: 'from-blue-500 to-cyan-500',
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'Bank-grade encryption and compliance with GDPR and SOC 2 standards',
    gradient: 'from-green-500 to-emerald-500',
  },
  {
    icon: Headphones,
    title: 'Multi-Channel',
    description: 'Deploy once, reach customers everywhere - SMS, voice, chat, WhatsApp',
    gradient: 'from-orange-500 to-red-500',
  },
];

export const Benefits: React.FC = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <section className="relative py-24">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Why Choose <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Looplet AI</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Everything you need to build and deploy intelligent AI assistants
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.1 }}
                className="relative group"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${benefit.gradient} opacity-10 group-hover:opacity-20 transition-opacity rounded-2xl blur-xl`} />
                <div className="relative bg-gray-800/50 backdrop-blur-lg border border-gray-700 rounded-2xl p-6 hover:border-gray-600 transition-colors h-full">
                  <div className={`w-12 h-12 mb-4 rounded-lg bg-gradient-to-br ${benefit.gradient} flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-white">{benefit.title}</h3>
                  <p className="text-gray-400">{benefit.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
